/**
 * DateRangePicker — presets + custom range pra filtros de analytics.
 */
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  label: string;
}

const PRESETS: { label: string; days: number; key: string }[] = [
  { label: "Hoje", days: 0, key: "today" },
  { label: "Últimos 7 dias", days: 7, key: "7d" },
  { label: "Últimos 30 dias", days: 30, key: "30d" },
  { label: "Últimos 90 dias", days: 90, key: "90d" },
  { label: "Últimos 12 meses", days: 365, key: "12m" },
];

export function presetRange(days: number, label: string): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return {
    from: format(from, "yyyy-MM-dd"),
    to: format(to, "yyyy-MM-dd"),
    label,
  };
}

interface Props {
  value: DateRange;
  onChange: (v: DateRange) => void;
}

export function DateRangePicker({ value, onChange }: Props) {
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          {value.label}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {PRESETS.map((p) => (
          <DropdownMenuItem
            key={p.key}
            onSelect={() => onChange(presetRange(p.days, p.label))}
            className={cn(value.label === p.label && "bg-primary-50 dark:bg-primary-500/10")}
          >
            {p.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="p-2 space-y-2" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">De</Label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Até</Label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8"
            />
          </div>
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              if (from && to) {
                const fromD = format(new Date(from), "dd/MM/yy", { locale: ptBR });
                const toD = format(new Date(to), "dd/MM/yy", { locale: ptBR });
                onChange({ from, to, label: `${fromD} → ${toD}` });
                setCustomOpen(false);
              }
            }}
          >
            Aplicar
          </Button>
        </div>
      </DropdownMenuContent>
      {/* Mantém open state pra UX */}
      <span hidden>{String(customOpen)}</span>
    </DropdownMenu>
  );
}
